import { Component } from '@theme/component';
import { CartLinesUpdateEvent, CartErrorEvent, StandardEvents } from '@shopify/events';

class CglBundleComponent extends Component {
  #controller;
  #busy = false;
  #variantPending = false;
  #generation = 0;
  #variants = {};
  #cameraVariant = '';

  connectedCallback() { super.connectedCallback(); this.#bind(); }
  updatedCallback() { super.updatedCallback(); this.#bind(); }
  disconnectedCallback() { super.disconnectedCallback(); this.#controller?.abort(); }
  get #scope() { return this.closest('.product-details') || this.parentElement; }
  get #form() { return this.#scope?.querySelector('product-form-component form[action*="/cart/add"]'); }
  get #select() { return this.querySelector('[data-bundle-accessory]'); }
  get #include() { return this.querySelector('[data-bundle-include]'); }
  get #camera() { return this.querySelector('[data-bundle-camera]'); }

  #bind() {
    this.#controller?.abort();
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    this.#variants = JSON.parse(this.querySelector('[data-bundle-variants]')?.textContent || '{}');
    this.#scope?.addEventListener('input', this.#onInput, { signal });
    this.#scope?.addEventListener('change', this.#onInput, { signal });
    this.closest('.shopify-section')?.addEventListener(StandardEvents.productSelect, this.#onVariantChange, { signal });
    this.querySelector('[data-cgl-bundle-submit]')?.addEventListener('click', this.#add, { signal });
    this.#update();
  }

  #selection() {
    const form = this.#form;
    const id = form?.querySelector('input[name="id"]')?.value || this.dataset.initialVariantId;
    const quantity = Number(form?.querySelector('[name="quantity"]')?.value || 1);
    const variant = this.#variants[id];
    const selected = this.#select?.selectedOptions[0];
    const chosen = selected?.value && !selected.disabled && this.#allowedSkus(variant).includes(selected.dataset.sku) ? selected : null;
    const included = Boolean(this.#include?.checked);
    return { form, id, quantity, variant, chosen, included, option: included ? chosen : null };
  }

  #onInput = (event) => {
    if (event.target === this.#camera) this.#include.checked = true;
    if (event.target === this.#select) this.#include.checked = Boolean(this.#select.value);
    if (event.target === this.#select || event.target === this.#include || event.target === this.#camera) this.querySelector('[data-cgl-bundle-status]').textContent = '';
    this.#update();
  };

  #allowedSkus(variant) {
    if (!variant) return [];
    if (variant.cameras?.length) return variant.profiles?.[this.#camera?.value] || [];
    return variant.targets || [];
  }

  #filterOffers(id, variant) {
    const cameras = variant?.cameras || [];
    if (this.#camera) this.#camera.disabled = this.#busy || this.#variantPending;
    const cameraRow = this.querySelector('[data-bundle-camera-row]');
    if (cameraRow) cameraRow.hidden = cameras.length === 0;
    if (this.#camera && this.#cameraVariant !== id) {
      const placeholder = this.#camera.options[0].cloneNode(true);
      this.#camera.replaceChildren(placeholder, ...cameras.map(camera => new Option(camera.name, camera.id)));
      this.#camera.value = '';
      this.#cameraVariant = id;
    }
    const allowed = this.#allowedSkus(variant);
    const options = [...(this.#select?.options || [])].filter(option => option.value);
    for (const option of options) {
      option.hidden = !allowed.includes(option.dataset.sku);
      option.disabled = option.hidden;
    }
    const available = options.filter(option => !option.disabled);
    if (!available.some(option => option.value === this.#select.value)) this.#select.value = available[0]?.value || '';
    const knownCamera = cameras.length === 0 || Boolean(this.#camera?.value);
    if (knownCamera && available.length === 0) this.#include.checked = false;
    this.#include.disabled = this.#busy || knownCamera && available.length === 0;
    this.#select.disabled = this.#busy || !available.length;
    const versionRow = this.querySelector('[data-bundle-version-row]');
    if (versionRow) versionRow.hidden = available.length < 2;
  }

  #money(cents) {
    return new Intl.NumberFormat(document.documentElement.lang || 'en', {
      style: 'currency', currency: this.dataset.currency || 'USD',
    }).format(cents / 100);
  }

  #onVariantChange = async (event) => {
    if (!this.#scope?.contains(event.target)) return;
    const generation = ++this.#generation;
    this.#variantPending = true;
    this.#update();
    try { await event.promise; }
    catch { /* The native picker owns variant fetch errors. */ }
    finally {
      if (generation === this.#generation && this.isConnected) {
        this.#variantPending = false;
        requestAnimationFrame(this.#update);
      }
    }
  };

  #update = () => {
    if (!this.isConnected) return;
    const initial = this.#selection();
    this.#filterOffers(initial.id, initial.variant);
    const { quantity, variant, option, chosen, included } = this.#selection();
    const button = this.querySelector('[data-cgl-bundle-submit]');
    const label = this.querySelector('[data-cgl-bundle-label]');
    const preview = this.querySelector('[data-bundle-accessory-preview]');
    const validQuantity = Number.isSafeInteger(quantity) && quantity > 0;
    this.hidden = !variant?.eligible;
    if (button) button.disabled = this.#busy || this.#variantPending || !variant?.available || !validQuantity || included && !chosen;
    this.#select.required = included;
    if (label && !this.#busy) label.textContent = this.#variantPending ? this.dataset.checking : this.dataset.add;
    if (variant) {
      const fitNote = this.querySelector('[data-bundle-fit-note]');
      if (fitNote) fitNote.textContent = variant.fitNote || '';
      const price = this.querySelector('[data-bundle-main-price]');
      if (price) price.textContent = this.#money(variant.price);
      const model = this.querySelector('[data-bundle-main-model]');
      if (model) model.textContent = variant.title === 'Default Title' ? '' : `(${variant.title})`;
      const mainLink = this.querySelector('[data-bundle-main-link]');
      if (mainLink && variant.url) mainLink.href = variant.url;
      const mainImage = mainLink?.querySelector('img');
      if (mainImage && variant.image) { mainImage.src = variant.image; mainImage.removeAttribute('srcset'); }
      const total = this.querySelector('[data-cgl-bundle-total]');
      if (total) total.textContent = this.#money((variant.price + Number(option?.dataset.price || 0)) * (validQuantity ? quantity : 1));
    }
    if (preview) preview.dataset.selected = String(Boolean(option));
    const price = this.querySelector('[data-bundle-accessory-price]');
    if (price) price.textContent = chosen ? this.#money(Number(chosen.dataset.price)) : '';
    const accessoryImage = preview?.querySelector('img');
    if (accessoryImage) accessoryImage.hidden = !chosen;
    if (!chosen || !preview) {
      this.querySelector('[data-bundle-accessory-title]').textContent = this.querySelector('[data-bundle-empty]')?.textContent || '';
      this.querySelector('[data-bundle-accessory-model]').textContent = '';
      if (preview) { preview.removeAttribute('href'); preview.setAttribute('aria-disabled', 'true'); }
      return;
    }
    preview.removeAttribute('aria-disabled');
    preview.href = chosen.dataset.url;
    const image = preview.querySelector('img');
    if (image) {
      image.hidden = !chosen.dataset.image;
      if (chosen.dataset.image) { image.src = chosen.dataset.image; image.removeAttribute('srcset'); }
      image.alt = chosen.dataset.title || '';
    }
    const combinedLabel = chosen.dataset.model?.startsWith(chosen.dataset.title);
    this.querySelector('[data-bundle-accessory-title]').textContent = combinedLabel ? chosen.dataset.model : chosen.dataset.title;
    this.querySelector('[data-bundle-accessory-model]').textContent = combinedLabel ? '' : chosen.dataset.model;
  };

  #add = async () => {
    if (this.#busy || this.#variantPending) return;
    const { form, id, quantity, variant, option, chosen, included } = this.#selection();
    const status = this.querySelector('[data-cgl-bundle-status]');
    if (!variant?.available || !variant.eligible || !form || !form.reportValidity()) return;
    if (included && !chosen) { this.#select.reportValidity(); return; }
    if (!Number.isSafeInteger(quantity) || quantity < 1 || option && (
      quantity < Number(option.dataset.min || 1) || (quantity - Number(option.dataset.min || 1)) % Number(option.dataset.step || 1) !== 0 ||
      option.dataset.max && quantity > Number(option.dataset.max)
    )) { status.textContent = this.dataset.quantityError; return; }
    const mainItem = { id: Number(id), quantity };
    const formData = new FormData(form);
    const properties = {};
    for (const [name, value] of formData) {
      if (name.startsWith('properties[') && name.endsWith(']') && typeof value === 'string') properties[name.slice(11, -1)] = value;
    }
    if (Object.keys(properties).length) mainItem.properties = properties;
    if (formData.get('selling_plan')) mainItem.selling_plan = Number(formData.get('selling_plan'));
    const items = option ? [mainItem, { id: Number(option.value), quantity }] : [mainItem];
    const sections = [...new Set([...document.querySelectorAll('cart-items-component[data-section-id]')].map(el => el.dataset.sectionId))].slice(0, 5);
    const deferred = CartLinesUpdateEvent.createPromise();
    this.#busy = true;
    this.#select.disabled = true;
    this.#include.disabled = true;
    this.#update();
    const button = this.querySelector('[data-cgl-bundle-submit]');
    button.setAttribute('aria-busy', 'true');
    this.querySelector('[data-cgl-bundle-label]').textContent = this.dataset.adding;
    status.textContent = '';
    this.dispatchEvent(new CartLinesUpdateEvent({ action: 'add', context: 'product',
      lines: items.map(item => ({ merchandiseId: String(item.id), quantity: item.quantity })), promise: deferred.promise }));
    const root = window.Shopify?.routes?.root || '/';
    let addAccepted = false;
    try {
      const response = await fetch(`${root}cart/add.js`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections, sections_url: location.pathname }),
      });
      const result = await response.json();
      const didError = !response.ok || Boolean(result.status);
      addAccepted = !didError;
      const cartResponse = await fetch(`${root}cart.js`, { cache: 'no-store' });
      if (!cartResponse.ok) throw new Error(this.dataset.error);
      const cart = await cartResponse.json();
      deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart), detail: {
        sections: result.sections, didError, source: 'cgl-bundle', items: cart.items, itemCount: cart.item_count,
      } });
      status.textContent = didError ? result.description || result.message || this.dataset.error : this.dataset.added;
      if (didError) this.dispatchEvent(new CartErrorEvent({ error: status.textContent, code: 'INVALID' }));
    } catch (error) {
      // Never retry an uncertain add automatically: reconcile the cart first.
      deferred.reject(error);
      if (addAccepted) { location.assign(`${root}cart`); return; }
      status.textContent = this.dataset.error;
      this.dispatchEvent(new CartErrorEvent({ error: this.dataset.error, code: 'SERVICE_UNAVAILABLE' }));
    } finally {
      this.#busy = false;
      this.#select.disabled = false;
      this.#include.disabled = false;
      button.removeAttribute('aria-busy');
      this.#update();
    }
  };
}
if (!customElements.get('cgl-bundle-component')) customElements.define('cgl-bundle-component', CglBundleComponent);
